import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Simple image upload: renders preview + button. Calls uploader(file) → { path }.
 * value: current storage path (or null)
 * onChange: (newPath|null) => void
 */
export function FileUpload({ label, value, onChange, uploader, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-1">
      {label && <div className="caption-label">{label}</div>}
      <div className="flex items-center gap-3">
        {value ? (
          <img src={fileUrl(value)} alt="" className="w-14 h-14 rounded object-cover ring-1 ring-stone-200" />
        ) : (
          <div className="w-14 h-14 rounded bg-stone-100 border border-dashed border-stone-300 flex items-center justify-center text-stone-400">
            <Upload className="w-4 h-4" />
          </div>
        )}
        <div className="flex gap-2">
          <input
            ref={ref}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={testId ? `${testId}-input` : undefined}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setBusy(true);
              try {
                const r = await uploader(f);
                onChange?.(r.path);
                toast.success("Uploaded");
              } catch (err) {
                toast.error("Upload failed");
              } finally {
                setBusy(false);
                if (ref.current) ref.current.value = "";
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => ref.current?.click()}
            data-testid={testId ? `${testId}-button` : undefined}
          >
            {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange?.(null)}
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
